/**
 * reassort.js — Reassort predictif (SPEC-FGOS-04, Option B)
 *
 * Modele :
 *   rendement_pages   = capacite_pages reelle (D1 Inkya / saisie) si connue,
 *                       sinon MOYENNE PAR FAMILLE (hypothese documentee ci-dessous)
 *   conso_pages_mois  = caEstimeParcMensuel (EUR HT) / COUT_PAGE_HT
 *                       (conversion grossiere montant -> volume, seule donnee dispo :
 *                       l'audit ne capture les pages/mois qu'en texte libre)
 *   autonomie_mois    = (rendement_pages x quantite) / conso_pages_mois
 *   date_reassort     = date_commande + autonomie_mois
 *
 * Badge "estimation approximative" (decision Wissam 2026-07-20) : porte par la
 * prediction UNIQUEMENT quand le rendement est une moyenne de famille (valeur
 * reelle absente). La conversion EUR->pages est toujours approximative mais ne
 * declenche pas le badge a elle seule — elle est mentionnee dans le detail.
 *
 * Le calcul part de la DERNIERE commande reelle (storage.listCommandes), jamais
 * de la date d'ouverture du compte — c'est tout l'objet de l'Option B.
 */

import { listCommandes, getProspects } from './storage.js';

// ------------------------------------------------------------
// Hypotheses documentees (a defaut de valeur reelle par reference)
// ------------------------------------------------------------

/** Cout moyen HT par page en consommable compatible (hypothese conversion EUR->pages). */
export const COUT_PAGE_HT = 0.02;

/**
 * Rendements moyens par famille (pages par unite). Hypotheses assumees,
 * affichees avec badge "estimation" quand utilisees :
 *  - cartouche jet d'encre compatible : ~200-300 p -> 250
 *  - toner laser compatible : ~1500-3000 p -> 2000
 *  - tambour (drum) : ~12000 p
 *  - ramette/carton papier : rendement EXACT par definition (500 f/ramette),
 *    detecte via la designation (ex "500F", "5 ramettes" -> 2500)
 */
export const RENDEMENT_FAMILLE = Object.freeze({
  toner: 2000,
  tambour: 12000,
  cartouche: 250,
});

/**
 * Famille rendement d'une ligne depuis sa designation (mots-cles, meme logique
 * que la categorisation SPEC-02). Retourne null si pas un consommable a pages
 * (papier photo, etiquettes, accessoire...) -> pas de prediction.
 */
export function familleRendement(designation) {
  const s = String(designation || '').toLowerCase();
  if (/tambour|\bdrum\b/.test(s)) return 'tambour';
  if (/toner/.test(s)) return 'toner';
  if (/ramette|papier\s?a4.*\d+\s?f/.test(s)) return 'ramette';
  if (/papier photo|etiquette|transfert/.test(s)) return null;
  if (/cartouche|encre|bouteille/.test(s)) return 'cartouche';
  return null;
}

/**
 * Rendement d'une ligne : reel si fourni, sinon moyenne famille.
 * @param {{designation?: string}} ligne
 * @param {number|null} rendementReel  capacite_pages connue (D1/saisie) ou null
 * @returns {{ pages: number, estime: boolean } | null}  null = pas predictible
 */
export function rendementLigne(ligne, rendementReel = null) {
  if (rendementReel != null && Number.isFinite(Number(rendementReel)) && Number(rendementReel) > 0) {
    return { pages: Number(rendementReel), estime: false };
  }
  const fam = familleRendement(ligne.designation);
  if (fam === 'ramette') {
    // Rendement exact par definition : N feuilles lisibles dans la designation.
    const m = String(ligne.designation || '').match(/(\d+)\s*ramettes?/i);
    const feuilles = String(ligne.designation || '').match(/(\d+)\s*f\b/i);
    const parRamette = feuilles ? Number(feuilles[1]) : 500;
    const nbRamettes = m ? Number(m[1]) : 1;
    return { pages: parRamette * nbRamettes, estime: false };
  }
  if (fam && RENDEMENT_FAMILLE[fam]) return { pages: RENDEMENT_FAMILLE[fam], estime: true };
  return null;
}

/**
 * Conso estimee en pages/mois d'un prospect. null si aucune donnee.
 */
export function consoPagesMois(prospect) {
  const ca = Number(prospect?.caEstimeParcMensuel);
  if (!Number.isFinite(ca) || ca <= 0) return null;
  return ca / COUT_PAGE_HT;
}

const JOURS_PAR_MOIS = 30.44;

/**
 * Predictions de reassort pour UNE commande.
 * @param {object} commande   { date, lignes[] }
 * @param {object} prospect
 * @param {(ref: string) => number|null} [lookupRendement]  capacite_pages reelle par ref
 * @returns {Array<{ref_interne, designation, quantite, rendementPages, rendementEstime,
 *                  autonomieMois, dateReassort: Date, estimation: boolean}>}
 */
export function computeReassortsCommande(commande, prospect, lookupRendement) {
  const conso = consoPagesMois(prospect);
  if (!conso || !commande?.date || !Array.isArray(commande.lignes)) return [];
  const t0 = new Date(commande.date).getTime();
  if (!Number.isFinite(t0)) return [];
  const out = [];
  for (const ligne of commande.lignes) {
    // Priorite : rendement fige sur la ligne a l'achat (enrichi depuis D1
    // capacite_pages au moment addCommande) > lookup externe > moyenne famille.
    const reel = (ligne.rendement_pages != null && Number(ligne.rendement_pages) > 0)
      ? Number(ligne.rendement_pages)
      : (lookupRendement ? lookupRendement(ligne.ref_interne) : null);
    const r = rendementLigne(ligne, reel);
    if (!r) continue;
    const qte = Math.max(1, Number(ligne.quantite) || 1);
    const autonomieMois = (r.pages * qte) / conso;
    const dateReassort = new Date(t0 + autonomieMois * JOURS_PAR_MOIS * 86400000);
    out.push({
      ref_interne: ligne.ref_interne,
      designation: ligne.designation || null,
      quantite: qte,
      pa_ht: ligne.pa_ht ?? null,
      rendementPages: r.pages,
      rendementEstime: r.estime,
      autonomieMois: Math.round(autonomieMois * 100) / 100,
      dateReassort,
      // Badge : rendement moyen de famille -> estimation approximative
      estimation: r.estime,
    });
  }
  return out;
}

/**
 * Reassorts a venir tous prospects, tries par date. Fonction d'entree unique
 * (Dashboard, fiche prospect, notifications, futur Brief matinal).
 *
 * @param {{horizonJours?: number, aujourdhui?: Date, lookupRendement?: Function,
 *          inclureEchus?: boolean}} [opts]
 * @returns {Array<{prospect, commande, prediction}>}
 */
export function getReassortsAVenir(opts = {}) {
  const { horizonJours = 14, aujourdhui = new Date(), lookupRendement = null, inclureEchus = true } = opts;
  const prospects = getProspects();
  const commandes = listCommandes();
  const limite = aujourdhui.getTime() + horizonJours * 86400000;
  const out = [];
  for (const p of prospects) {
    const cs = commandes.filter((c) => c.prospectId === p.id);
    if (!cs.length) continue;
    const derniere = cs.reduce((a, b) => (String(a.date) >= String(b.date) ? a : b));
    for (const pred of computeReassortsCommande(derniere, p, lookupRendement)) {
      const t = pred.dateReassort.getTime();
      if (t <= limite && (inclureEchus || t >= aujourdhui.getTime())) {
        out.push({ prospect: p, commande: derniere, prediction: pred });
      }
    }
  }
  out.sort((a, b) => a.prediction.dateReassort - b.prediction.dateReassort);
  return out;
}
